<template>
  <div class="main-page">
    <organization-breadcrumb :items="[getCurrentClass.name, getCurrentAssignment.name]" />
    <div id="page-content" class="mt-px-24" v-loading.fullscreen.lock="loading">
      <el-row type="flex" justify="center">
        <el-col :xs="24" :md="14">
          <router-link :to="{ name: 'class-details', hash: '#assignment' }" class="back-history">
            <i class="el-icon-back"></i> {{ $t("assignments.details.back_to_assignment_list") }}
          </router-link>
          <div class="page-header flex justify-between items-center">
            <rename-form
              v-if="editMode"
              :old-name="getCurrentAssignment.name"
              v-on:cancel-rename="cancelRename"
              v-on:handle-edit-name="handleEditName"
            >
            </rename-form>
            <p v-else class="page-name">
              {{ getCurrentAssignment.name }}
              <span class="edit-name" @click="() => (editMode = true)" :title="$t('assignments.details.edit_timeline')">
                <i class="elsa-icon-edit2" />
              </span>
            </p>
            <div>
              <el-button class="remove-btn" @click="handleDeleteAssignment()">
                {{ $t(`common.delete`) }}
              </el-button>
            </div>
          </div>
          <section>
            <header v-if="assignmentType === 'ai_tutor'" class="section-header text-header-center">
              {{ $t("ai_tutor.title.assignment_detail") }}
            </header>
            <header v-else class="section-header">{{ $t("assignments.details.information") }}</header>
            <assignment-info
              :time-line="timeLine"
              :reminder-sent="getCurrentAssignment.reminder_before_day"
              :study-sets="getCurrentAssignment.study_sets"
              :class-name="getCurrentClass.name"
              @update-timeline="handleUpdateTimeline"
            />
          </section>
          <template v-if="assignmentType === 'lesson'">
            <section>
              <header class="section-header">{{ $t("assignments.details.overall_result") }}</header>
              <assignment-base-box>
                <assignment-overall :overall="overallData" />
              </assignment-base-box>
            </section>
            <section v-if="lessons && lessons.length">
              <header class="section-header">{{ $t("assignments.details.detail_result") }}</header>
              <assignment-base-box>
                <assignment-detail :lessons="lessons" />
              </assignment-base-box>
            </section>
          </template>
          <template v-else-if="assignmentType === 'ai_tutor'">
            <section>
              <header class="section-header text-header-center">
                {{ $t("ai_tutor.title.overall_assignment_results") }}
              </header>
              <assignment-base-box>
                <assignment-overall :overall="overallData" />
              </assignment-base-box>
            </section>
            <section v-if="aiTutors && aiTutors.length">
              <header class="section-header text-header-center">{{ $t("ai_tutor.title.lesson_performance") }}</header>
              <assignment-base-box>
                <assignment-a-i-tutor-detail :lesson="aiTutors[0]" />
              </assignment-base-box>
            </section>
          </template>
          <section v-else>
            <section v-if="showAssessmentDetail && assessments && assessments.length">
              <header class="section-header">{{ $t("assignments.details.assessment_detail_header") }}</header>
              <assignment-base-box>
                <ul class="assessment-detail" v-for="assessment in assessments" :key="assessment.id">
                  <li>{{ assessment.name }}</li>
                </ul>
              </assignment-base-box>
            </section>
          </section>
        </el-col>
      </el-row>
      
      <section v-if="getCurrentAssignment.performances && getCurrentAssignment.performances.length">
        <div class="wrap-table-ai-performance">
          <div v-if="assignmentType === 'ai_tutor'">
            <header class="section-header text-header-center">{{ $t("ai_tutor.title.individual_performance") }}</header>
            <assignment-performance-a-i
              :performances="currPerformances"
              :study-sets="studySets.map(item => item.id)"
              :assignment-type="assignmentType"
              :currAssignment="getCurrentAssignment"
            />
          </div>
          <div v-if="assignmentType === 'study_set' || assignmentType === 'read_aloud'">
            <header class="section-header">{{ $t("assignments.details.performance") }}</header>
            <assignment-performance
              :performances="getCurrentAssignment.performances"
              :study-sets="studySets.map(item => item.id)"
              :assignment-type="assignmentType"
            />
          </div>

          <section v-if="assignmentType === 'lesson'">
            <header class="section-header">{{ $t("assignments.details.performance_lesson") }}</header>
            <assignment-base-box>
              <assignment-performance-lesson
                :performances="getCurrentAssignment.performances"
                :lesson-ids="lessons.map(item => item.original_id)"
              />
            </assignment-base-box>
          </section>
        </div>
      </section>
    </div>
    <confirm-delete-assignment-modal
      page="detail"
      :dialogVisible="modals.showConfirmDelete"
      append-to-body
      v-on:closed="modals.showConfirmDelete = false"
      v-on:delete="handleConfirmDeleteAssignment"
    />
    <update-timeline-assignment-modal
      :dialogVisible="modals.showUpdateTimeline"
      append-to-body
      v-on:closed="modals.showUpdateTimeline = false"
    />
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import { successMessage, warningMessage } from "@/utils/messages";
import { path } from "ramda";
import OrganizationBreadcrumb from "@/components/common/OrganizationBreadcrumb";
import AssignmentInfo from "@/components/assignments/AssignmentInfo";
import AssignmentBaseBox from "@/components/assignments/AssignmentBaseBox";
import AssignmentPerformance from "@/components/assignments/AssignmentPerformance";
import AssignmentPerformanceAI from "@/components/assignments/AssignmentPerformanceAI";
import AssignmentPerformanceLesson from "@/components/assignments/AssignmentPerformanceLesson";
import AssignmentOverall from "@/components/assignments/AssignmentOverall";
import AssignmentDetail from "@/components/assignments/AssignmentDetail";
import AssignmentAITutorDetail from "@/components/assignments/AssignmentAITutorDetail";
import ConfirmDeleteAssignmentModal from "@/components/modals/assignments/ConfirmDeleteAssignmentModal";
import UpdateTimelineAssignmentModal from "@/components/modals/assignments/UpdateTimelineAssignmentModal";
import RenameForm from "@/components/common/RenameForm";
import { SUBMISSION_LATE, SUBMISSION_MISSED, SUBMISSION_ON_TIME } from "@/constants/generic";

export default {
  components: {
    OrganizationBreadcrumb,
    AssignmentInfo,
    AssignmentPerformance,
    AssignmentPerformanceAI,
    ConfirmDeleteAssignmentModal,
    UpdateTimelineAssignmentModal,
    AssignmentBaseBox,
    AssignmentOverall,
    AssignmentDetail,
    AssignmentPerformanceLesson,
    RenameForm,
    AssignmentAITutorDetail,
  },
  computed: {
    ...mapGetters({
      getCurrentClass: "classes/getCurrentClass",
      getCurrentAssignment: "assignments/getCurrentAssignment",
      AIPerformance: "assignments/AIPerformance",
      currentOrganizationType: "organization/currentOrganizationType",
    }),
    timeLine() {
      return {
        startDate: path(["getCurrentAssignment", "start_date"])(this),
        dueDate: path(["getCurrentAssignment", "due_date"])(this),
      };
    },
    lessons() {
      return this.getCurrentAssignment.lessons || [];
    },
    assessments() {
      return this.getCurrentAssignment.assessments || [];
    },
    assignmentType() {
      return this.getCurrentAssignment.assignment_type;
    },
    performances() {
      return this.getCurrentAssignment.performances;
    },
    aiTutors() {
      return this.getCurrentAssignment.ai_tutors;
    },
    overallData() {
      const totalStudents = this.performances.length;
      const submittedPerformances = this.performances.filter(item => item.score);
      const scores = submittedPerformances.map(item => item.score);
      const totalStudentsSubmitted = submittedPerformances.length;
      const totalScores = scores.reduce((acc, curr) => {
        return acc + curr;
      }, 0);

      const minScore = Math.round(Math.min(...scores)) | 0;
      const maxScore = Math.round(Math.max(...scores)) | 0;
      const averageScore = Math.round(totalScores / totalStudentsSubmitted) | 0;
      const totalSubmissionOntime = this.performances.filter(
        ({ submission_status }) => submission_status === SUBMISSION_ON_TIME
      ).length;
      const totalSubmissionLate = this.performances.filter(
        ({ submission_status }) => submission_status === SUBMISSION_LATE
      ).length;
      const totalSubmissionMissed = this.performances.filter(
        ({ submission_status }) => submission_status === SUBMISSION_MISSED
      ).length;

      return {
        maxScore,
        minScore,
        averageScore,
        totalSubmissionLate,
        totalSubmissionOntime,
        totalSubmissionMissed,
        totalStudents,
      };
    },
    studySets() {
      return this.getCurrentAssignment.study_sets || [];
    },
    currPerformances() {
      return this.getCurrentAssignment && this.getCurrentAssignment.performances;
    },
  },
  data() {
    return {
      modals: {
        showConfirmDelete: false,
        showUpdateTimeline: false,
        showConfirmEditName: false,
      },
      loading: true,
      editMode: false,
      showAssessmentDetail: false
    };
  },
  props: {
    activeComponentProp: {},
    classId: {},
    assignmentId: {},
    organizationId: {},
  },
  async mounted() {
    this.$store.dispatch("assignments/getAssignment", { classId: this.classId, assignmentId: this.assignmentId });
    try {
      await this.$store.dispatch("classes/getClass", {
      organizationId: this.organizationId,
      groupId: this.classId,
    });
    } catch (error) {
      this.loading = false;
    }
    this.loading = false;
  },
  methods: {
    ...mapActions({
      deleteAssignment: "assignments/deleteAssignment",
      updateAssignment: "assignments/updateAssignment",
      resetAssignment: "assignments/resetCurrentAssignment",
    }),
    async handleConfirmDeleteAssignment() {
      try {
        await this.deleteAssignment(this.assignmentId);
        successMessage(this.$t("assignments.delete_assignment_success"))(this);
        return this.backToList();
      } catch (error) {
        return warningMessage(this.$t("assignments.delete_assignment_warning"))(this);
      }
    },
    handleDeleteAssignment() {
      this.modals.showConfirmDelete = true;
    },
    backToList() {
      this.$router.push({
        name: "classes",
        params: {
          organizationId: this.currentOrganizationId,
        },
      });
    },
    async handleUpdateTimeline(payload) {
      this.loading = true;
      try {
        await this.updateAssignment({
          classId: this.classId,
          assignmentId: this.assignmentId,
          payload: payload,
        });
        this.modals.showUpdateTimeline = true;
      } catch (error) {
        return false;
      } finally {
        this.loading = false;
      }
    },
    async handleEditName(newName) {
      const payload = { name: newName };
      this.loading = true;
      this.editMode = false;
      try {
        await this.updateAssignment({
          classId: this.classId,
          assignmentId: this.assignmentId,
          payload: payload,
        });
        this.$store.dispatch("assignments/getAssignment", { classId: this.classId, assignmentId: this.assignmentId });
        successMessage(this.$t("common.update_name_success"))(this);
      } catch (error) {
        warningMessage(this.$t("common.generic_error_message"))(this);
        return false;
      } finally {
        this.loading = false;
        this.modals.showConfirmEditName = false;
      }
    },
    cancelRename() {
      this.editMode = false;
    },
  },
};
</script>

<style lang="scss" scoped>
@import "../scss/_colors.scss";
.main-page {
  overflow: hidden;
  width: 100%;
}

.page-name {
  font-weight: 700;
  font-size: 30px;
  line-height: 45px;
  color: $black;
  margin-top: 0px;
  margin-bottom: 0px;
  display: flex;
}

.page-header {
  margin-bottom: 24px;
  margin-top: 16px;
}
.section-header {
  font-weight: 700;
  font-size: 20px;
  line-height: 30px;
  color: $black;
  margin-bottom: 8px;
}

.text-header-center {
  text-align: center;
}

.remove-btn {
  color: $red;
  font-size: 16px;
  font-weight: 600;
  line-height: 24px;
  font-family: "SF Pro Display";
  padding: 12px 32px;
  border-radius: 8px;
}

@media only screen and (max-width: 992px) {
  .component-wrapper {
    margin-top: 30px;
  }
}
.edit-name {
  color: #478dff;
  cursor: pointer;
  padding: 0px 13px;
  margin-top: 6px;
}
.assessment-detail {
  list-style: none;
}

.wrap-table-ai-performance {
  padding: 0 40px;
}
</style>
